/**
 * Tickets Page - Support ticket dashboard
 * School Admin: Create and view own tickets
 * Super Admin: View, manage, and close all tickets
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import ticketService from '../services/ticketService';
import { Plus, CircleNotch, CaretDown } from '@phosphor-icons/react';
import './TicketsPage.css';

function TicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'technical',
    priority: 'medium'
  });

  // Load tickets
  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ticketService.getTickets(page, 20, filterStatus, filterCategory);
      setTickets(data.data || []);
      setTotalPages(data.pages || 1);
      
      // Load stats
      const statsData = await ticketService.getTicketStats();
      setStats(statsData.data || {});
    } catch (error) {
      toast.error('Failed to load tickets: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterCategory]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const result = await ticketService.createTicket(formData);
      toast.success(result.message || 'Ticket created successfully!');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', category: 'technical', priority: 'medium' });
      setPage(1);
      loadTickets();
    } catch (error) {
      toast.error('Failed to create ticket: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const detail = await ticketService.getTicketDetail(ticket.id);
      setSelectedTicket(detail.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load ticket details: ' + error.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    
    try {
      await ticketService.updateTicketStatus(selectedTicket.id, newStatus);
      toast.success('Ticket status updated!');
      setShowDetailModal(false);
      loadTickets();
    } catch (error) {
      toast.error('Failed to update status: ' + error.message);
    }
  };

  const handleAddComment = async (commentText, isInternal) => {
    if (!selectedTicket || !commentText.trim()) return;
    
    try {
      await ticketService.addComment(selectedTicket.id, commentText, isInternal);
      toast.success('Comment added!');
      // Reload ticket detail
      const detail = await ticketService.getTicketDetail(selectedTicket.id);
      setSelectedTicket(detail.data);
    } catch (error) {
      toast.error('Failed to add comment: ' + error.message);
    }
  };

  const handleCloseTicket = async (resolutionText) => {
    if (!selectedTicket || !resolutionText.trim()) return;
    
    try {
      await ticketService.closeTicket(selectedTicket.id, resolutionText);
      toast.success('Ticket closed!');
      setShowDetailModal(false);
      loadTickets();
    } catch (error) {
      toast.error('Failed to close ticket: ' + error.message);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#3b82f6',
      medium: '#f59e0b',
      high: '#ef4444',
      urgent: '#dc2626'
    };
    return colors[priority] || '#6b7280';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#6b7280',
      in_progress: '#3b82f6',
      resolved: '#10b981',
      closed: '#10b981'
    };
    return colors[status] || '#6b7280';
  };

  return (
    <Layout>
      <div className="page tickets-page">
        <div className="page-header">
          <div>
            <h1>Support Tickets</h1>
            <p>Manage support requests and communication with School ERP team</p>
          </div>
          {user && (user.role === 'school_admin' || user.role === 'super_admin') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} weight="bold" /> Create Ticket
            </button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.open || 0}</div>
            <div className="stat-label">Open</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.in_progress || 0}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.resolved || 0}</div>
            <div className="stat-label">Resolved</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.closed || 0}</div>
            <div className="stat-label">Closed</div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <select 
            value={filterStatus || ''} 
            onChange={(e) => { setFilterStatus(e.target.value || null); setPage(1); }}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select 
            value={filterCategory || ''} 
            onChange={(e) => { setFilterCategory(e.target.value || null); setPage(1); }}
            className="filter-select"
          >
            <option value="">All Categories</option>
            <option value="technical">Technical</option>
            <option value="academic">Academic</option>
            <option value="finance">Finance</option>
            <option value="hr">HR</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Tickets Table */}
        {loading ? (
          <div className="loading">
            <CircleNotch size={32} weight="bold" /> Loading tickets...
          </div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
            <p>No tickets found</p>
            {user && (user.role === 'school_admin' || user.role === 'super_admin') && (
              <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                Create Your First Ticket
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="tickets-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="ticket-row">
                      <td className="ticket-id">{ticket.id.slice(0, 8)}...</td>
                      <td className="ticket-title">{ticket.title}</td>
                      <td>{ticket.category}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getPriorityColor(ticket.priority) }}>
                          {ticket.priority.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getStatusColor(ticket.status) }}>
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                      <td>
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewTicket(ticket)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button 
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="btn btn-sm"
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button 
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="btn btn-sm"
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* Create Ticket Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Create Support Ticket</h2>
              <form onSubmit={handleCreateSubmit}>
                <div className="form-group">
                  <label>Category *</label>
                  <select 
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    <option value="technical">Technical</option>
                    <option value="academic">Academic</option>
                    <option value="finance">Finance</option>
                    <option value="hr">HR</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority *</label>
                  <select 
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Title *</label>
                  <input 
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Brief title of your issue"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed description of your issue"
                    rows="6"
                    required
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Detail Modal */}
        {showDetailModal && selectedTicket && (
          <TicketDetailModal 
            ticket={selectedTicket}
            userRole={user?.role}
            onClose={() => setShowDetailModal(false)}
            onStatusChange={handleStatusChange}
            onAddComment={handleAddComment}
            onCloseTicket={handleCloseTicket}
          />
        )}
      </div>
    </Layout>
  );
}

/**
 * Ticket Detail Modal Component
 */
function TicketDetailModal({ ticket, userRole, onClose, onStatusChange, onAddComment, onCloseTicket }) {
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [resolutionText, setResolutionText] = useState('');
  const [showResolutionForm, setShowResolutionForm] = useState(false);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{ticket.title}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <div className="ticket-detail">
          {/* Header Info */}
          <div className="ticket-info-grid">
            <div>
              <strong>ID:</strong>
              <span>{ticket.id.slice(0, 8)}...</span>
            </div>
            <div>
              <strong>Category:</strong>
              <span>{ticket.category}</span>
            </div>
            <div>
              <strong>Priority:</strong>
              <span className="badge" style={{ backgroundColor: getPriorityColor(ticket.priority) }}>
                {ticket.priority.toUpperCase()}
              </span>
            </div>
            <div>
              <strong>Status:</strong>
              <span className="badge" style={{ backgroundColor: getStatusColor(ticket.status) }}>
                {ticket.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div>
              <strong>Created:</strong>
              <span>{new Date(ticket.created_at).toLocaleString()}</span>
            </div>
            {ticket.closed_at && (
              <div>
                <strong>Closed:</strong>
                <span>{new Date(ticket.closed_at).toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="section">
            <h3>Description</h3>
            <p>{ticket.description}</p>
          </div>

          {/* Resolution */}
          {ticket.resolution_summary && (
            <div className="section resolution">
              <h3>Resolution</h3>
              <p>{ticket.resolution_summary}</p>
            </div>
          )}

          {/* Comments */}
          <div className="section">
            <h3>Comments & Updates</h3>
            <div className="comments-list">
              {ticket.comments && ticket.comments.length > 0 ? (
                ticket.comments.map((comment, idx) => (
                  <div key={idx} className={`comment ${comment.is_internal ? 'internal' : ''}`}>
                    <div className="comment-header">
                      <strong>{comment.author_id}</strong>
                      {comment.is_internal && <span className="badge internal">Internal</span>}
                      <span className="time">{new Date(comment.created_at).toLocaleString()}</span>
                    </div>
                    <p>{comment.comment}</p>
                  </div>
                ))
              ) : (
                <p className="empty">No comments yet</p>
              )}
            </div>

            {/* Add Comment */}
            {ticket.status !== 'closed' && (
              <div className="add-comment">
                <textarea 
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  rows="3"
                />
                {userRole && userRole === 'super_admin' && (
                  <label>
                    <input 
                      type="checkbox" 
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                    />
                    Internal comment
                  </label>
                )}
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    onAddComment(commentText, isInternal);
                    setCommentText('');
                    setIsInternal(false);
                  }}
                  disabled={!commentText.trim()}
                >
                  Post Comment
                </button>
              </div>
            )}
          </div>

          {/* Actions (Super Admin Only) */}
          {userRole && userRole === 'super_admin' && ticket.status !== 'closed' && (
            <div className="section actions">
              <h3>Actions</h3>
              <div className="action-buttons">
                {ticket.status === 'open' && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => onStatusChange('in_progress')}
                  >
                    Mark as In Progress
                  </button>
                )}
                
                {ticket.status === 'in_progress' && (
                  <button 
                    className="btn btn-success"
                    onClick={() => onStatusChange('resolved')}
                  >
                    Mark as Resolved
                  </button>
                )}

                {ticket.status !== 'closed' && (
                  <>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowResolutionForm(!showResolutionForm)}
                    >
                      {showResolutionForm ? 'Cancel' : 'Close Ticket'}
                    </button>

                    {showResolutionForm && (
                      <div className="resolution-form">
                        <textarea 
                          value={resolutionText}
                          onChange={(e) => setResolutionText(e.target.value)}
                          placeholder="Enter resolution summary..."
                          rows="3"
                        />
                        <button 
                          className="btn btn-primary"
                          onClick={() => {
                            onCloseTicket(resolutionText);
                            setResolutionText('');
                            setShowResolutionForm(false);
                          }}
                          disabled={!resolutionText.trim()}
                        >
                          Confirm Close
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getPriorityColor(priority) {
  const colors = {
    low: '#3b82f6',
    medium: '#f59e0b',
    high: '#ef4444',
    urgent: '#dc2626'
  };
  return colors[priority] || '#6b7280';
}

function getStatusColor(status) {
  const colors = {
    open: '#6b7280',
    in_progress: '#3b82f6',
    resolved: '#10b981',
    closed: '#10b981'
  };
  return colors[status] || '#6b7280';
}

export default TicketsPage;
